import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  Modal,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import { useParams, useNavigate } from "react-router-dom";
import DataApi from "../../api/dataApi";
import Loader from "./Loader";
import ScrollToTop from "./ScrollToTop";
import PubSub from "pubsub-js";
import { useLocation } from "react-router-dom";
import ConfirmationModal from "./ConfirmationModal";
import { COUNTRY_CODES } from "../../constants/COUNTRY_CODES";
import { convertToUserTimezone } from "../../utils/convertTimeZone";
import { Country, State } from "country-state-city";
const LOOKUP_ENDPOINT_MAP = {
  authors: "author",
  author: "author",
  categories: "category",
  category: "category",
  users: "user",
  user: "user",
  states: "state",
  state: "state",
  cities: "city",
  city: "city",
  vendors: "vendor",
  vendor: "vendor",
  roles: "user-role",
  modules: "module",
  module: "module",
  departments: "department",
  department: "department",
  libraries: "library",
  library: "library",
  subscriptions: "subscriptions",
  subscription: "subscriptions",
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data?.data) {
    return Array.isArray(payload.data.data) ? payload.data.data : [];
  }
  if (Array.isArray(payload.rows)) return payload.rows;
  if (payload.records && Array.isArray(payload.records)) return payload.records;
  if (payload.book_copies && Array.isArray(payload.book_copies)) return payload.book_copies;
  return [];
};



// const normalizeLocationData = (record) => {
//   if (!record) return record;
//
//   let updated = { ...record };
//
//   // Convert country name → ISO
//   if (record.country && record.country.length > 2) {
//     const countryObj = Country.getAllCountries()
//       .find(c => c.name.toLowerCase() === record.country.toLowerCase());
//
//     if (countryObj) {
//       updated.country = countryObj.isoCode;
//     }
//   }
//
//   // Convert state name → ISO
//   if (record.state && updated.country) {
//     const stateObj = State.getStatesOfCountry(updated.country)
//       .find(s => s.name.toLowerCase() === record.state.toLowerCase());
//
//     if (stateObj) {
//       updated.state = stateObj.isoCode;
//     }
//   }
//
//   return updated;
// };


const getOptionValue = (option = {}) => {
  return (
    option.id ??
    option.value ??
    option.code ??
    option.uuid ??
    option.email ??
    option.key ??
    option.slug ??
    option.name ??
    option.label ??
    option.firstname ??
    option.lastname ??
    ""
  );
};

const getOptionDisplayLabel = (option = {}, field = {}) => {
  if (field.displayKey && option[field.displayKey]) return option[field.displayKey];
  if (option.label) return option.label;
  if (option.name) return option.name;
  if (option.title) return option.title;
  if (option.author_name) return option.author_name;
  if (option.publisher_name) return option.publisher_name;
  if (option.classification_name) return option.classification_name;
  if (option.displayName) return option.displayName;
  if (option.firstname || option.lastname) {
    const composed = `${option.firstname || ""} ${option.lastname || ""}`.trim();
    if (composed) return composed;
  }
  if (option.email) return option.email;
  if (option.value) return option.value;
  if (option.id) return String(option.id);
  if (option.role_name) return option.role_name;
  return "Item";
};

const toStringSafe = (value) => {
  if (value === undefined || value === null) return "";
  return value.toString();
};


const getCountryInfo = (countryCode) => {
  if (!countryCode) return { timezone: null, currency: null };


  const cleanCode = countryCode.toString().trim();


  const countryInfo = COUNTRY_CODES.find(country => {
    if (!country) return false;


    if (country.code && country.code.toString().toUpperCase() === cleanCode.toUpperCase()) {
      return true;
    }


    if (country.name && typeof country.name === 'string') {
      return country.name.toLowerCase() === cleanCode.toLowerCase();
    }

    return false;
  });

  if (countryInfo) {
    return {
      timezone: countryInfo.timezone || null,
      currency: countryInfo.currency || null
    };
  }


  const fallbackMap = {
    'US': { timezone: 'America/New_York', currency: 'USD' },
    'GB': { timezone: 'Europe/London', currency: 'GBP' },
    'IN': { timezone: 'Asia/Kolkata', currency: 'INR' },
    'AU': { timezone: 'Australia/Sydney', currency: 'AUD' },
    'CA': { timezone: 'America/Toronto', currency: 'CAD' },
    'DE': { timezone: 'Europe/Berlin', currency: 'EUR' },
    'FR': { timezone: 'Europe/Paris', currency: 'EUR' },
    'JP': { timezone: 'Asia/Tokyo', currency: 'JPY' },
    'CN': { timezone: 'Asia/Shanghai', currency: 'CNY' },
    'AF': { timezone: 'Asia/Kabul', currency: 'AFN' }, // Afghanistan
  };

  return fallbackMap[cleanCode] || { timezone: null, currency: null };
};

const ModuleDetail = ({
  moduleHeader,
  moduleName,
  moduleApi,
  moduleLabel,
  icon,
  validationRules,
  fields,
  relatedModules = [],
  customHeader = null,
  customSections = [],
  bookStatistics = [],
  customActions = [],
  onEdit = null,
  onDelete = null,
  imageField = null,
  imageUrl = null,
  lookupNavigation = {},
  externalData = {},
  setIsEditable,
  timeZone,
  permissions = {},
  onTempDataChange = null,
}) => {
  const {
    allowView = true,
    allowCreate = true,
    allowEdit = true,
    allowDelete = true,
  } = permissions;
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [relatedData, setRelatedData] = useState({});
  const [isEditing, setIsEditing] = useState(
    location?.state?.isEdit ? location?.state?.isEdit : false
  );
  const [tempData, setTempData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lookupOptions, setLookupOptions] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [userNames, setUserNames] = useState({});
  const [userAvatars, setUserAvatars] = useState({});


  const [passwordVisibility, setPasswordVisibility] = useState({});

  const moduleNameFromUrl = window.location.pathname.split("/")[1];

  const nonEditableFields = useMemo(
    () => [
      "createdbyid",
      "createddate",
      "lastmodifiedbyid",
      "lastmodifieddate",
      "created_by",
      "created_at",
      "modified_by",
      "modified_at",
      "createdby",
      "createdat",
      "lastmodifiedby",
      "lastmodifiedat",
    ],
    []
  );

  const allFieldGroups = useMemo(() => {
    const groups = [];
    const appendFields = (list) => {
      if (Array.isArray(list)) {
        groups.push(...list);
      }
    };
    appendFields(fields?.overview);
    appendFields(fields?.details);
    appendFields(fields?.other);
    appendFields(fields?.address);
    return groups;
  }, [fields]);

  const selectOptionKeys = useMemo(() => {
    const keys = new Set();
    allFieldGroups.forEach((field) => {
      if (field?.type === "select" && typeof field.options === "string") {
        keys.add(field.options);
      }
    });
    return Array.from(keys);
  }, [allFieldGroups]);

  const UserAvatar = ({ userId, size = 32, showName = true, clickable = true }) => {
    const userName = userNames[userId] || `User ${userId}`;
    const userAvatar =
      userAvatars[userId] ||
      `https://ui-avatars.com/api/?name=User&background=11439b&color=fff&size=${size}`;

    const handleUserClick = (e) => {
      e.stopPropagation();
      if (clickable && userId) {
        navigate(`/user/${userId}`);
      }
    };

    return (
      <div
        className={`d-flex align-items-center ${clickable ? "cursor-pointer" : ""
          }`}
        onClick={handleUserClick}
        style={{
          cursor: clickable ? "pointer" : "default",
          textDecoration: "none",
          gap: "8px",

        }}
      >
        <img
          src={userAvatar}
          alt={userName}
          className="rounded-circle"
          style={{
            width: size,
            height: size,
            objectFit: "cover",
            border: "2px solid #e9ecef",
          }}
        />
        {showName && (
          <span
            className="fw-medium"
            style={{
              color: clickable ? "var(--primary-color)" : "#495057",
              textDecoration: clickable ? "none" : "none",
            }}
            onMouseEnter={(e) => {
              if (clickable) e.target.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              if (clickable) e.target.style.textDecoration = "none";
            }}
          >
            {userName}
          </span>
        )}
      </div>
    );
  };

  const fetchUserNames = async (userIds) => {
    try {
      const userApi = new DataApi("user");
      const names = {};
      const avatars = {};

      const uniqueUserIds = [...new Set(userIds.filter((id) => id && id !== ""))];

      for (const userId of uniqueUserIds) {
        try {
          const response = await userApi.fetchById(userId);
          if (response && response.data) {
            const user = response.data.success
              ? response.data.data
              : response.data;
            if (user) {
              const fullName = `${user.firstname || ""} ${user.lastname || ""
                }`.trim();
              names[userId] = fullName || user.email || `User ${userId}`;

              if (user.profile_picture) {
                avatars[userId] = user.profile_picture;
              } else {
                const initials = fullName
                  ? fullName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                  : "U";
                avatars[userId] = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  fullName || "User"
                )}&background=11439b&color=fff&size=32`;
              }
            }
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          names[userId] = `User ${userId}`;
          avatars[userId] =
            "https://ui-avatars.com/api/?name=User&background=11439b&color=fff&size=32";
        }
      }

      setUserNames((prev) => ({ ...prev, ...names }));
      setUserAvatars((prev) => ({ ...prev, ...avatars }));
    } catch (error) {
      console.error("Error fetching user names:", error);
    }
  };

  const fetchData = async () => {
    try {
      const api = new DataApi(moduleApi);
      const response = await api.fetchById(id);


      if (response && response.data) {
        const responseData = response.data;

        let finalData = null;

        if (responseData.success && responseData.data) {
          finalData = responseData.data;
        } else if (
          responseData.data &&
          responseData.data.success &&
          responseData.data.data
        ) {
          finalData = responseData.data.data;
        } else if (responseData.data) {
          finalData = responseData.data;
        } else if (responseData.id) {
          finalData = responseData;
        } else if (Array.isArray(responseData) && responseData.length > 0) {
          finalData = responseData[0];
        } else {
          finalData = responseData;
        }

        // const normalized = normalizeLocationData(finalData);
        setData(finalData);

      }

    } catch (error) {
      console.error(`Error fetching ${moduleLabel}:`, error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: `Failed to fetch ${moduleLabel} details`,
      });
    }
  };

  const fetchRelatedData = async () => {
    try {
      const relatedDataObj = {};
      const normalizedModules = relatedModules.map((module) => {
        if (typeof module === "string") {
          return {
            key: module,
            api: module,
            label: module.charAt(0).toUpperCase() + module.slice(1),
          };
        }
        return module;
      });

      for (const relatedModule of normalizedModules) {

        try {
          if (!relatedModule.api) continue;
          const api = new DataApi(relatedModule.api);
          const response = await api.get(
            relatedModule.endpoint ? relatedModule.endpoint(id) : `/${id}`
          );
          if (response && response.data) {
            const responseData = response.data.success
              ? response.data.data
              : response.data;
            relatedDataObj[relatedModule.key] = normalizeListResponse(responseData);
          }
        } catch (error) {
          console.error(
            `Error fetching related ${relatedModule.label || relatedModule.key
            }:`,
            error
          );
          relatedDataObj[relatedModule.key] = [];
        }
      }
      setRelatedData(relatedDataObj);
    } catch (error) {
      console.error("Error fetching related data:", error);
    }
  };

  useEffect(() => {

    const loadData = async () => {
      try {
        setLoading(true);
        await fetchData();

        if (relatedModules.length > 0) {
          await fetchRelatedData();
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id && moduleApi) {
      loadData();
    } else {
      console.error("Missing id or moduleApi:", { id, moduleApi });
      setLoading(false);
    }
  }, [id, moduleApi, moduleLabel]);

  useEffect(() => {
    if (!data) return;

    const userIds = [];

    if (data.createdbyid) userIds.push(data.createdbyid);
    if (data.lastmodifiedbyid) userIds.push(data.lastmodifiedbyid);
    if (data.created_by) userIds.push(data.created_by);
    if (data.modified_by) userIds.push(data.modified_by);

    if (userIds.length > 0) {
      fetchUserNames(userIds);
    }
  }, [data]);


  useEffect(() => {
    let isMounted = true;

    const loadLookupData = async () => {
      if (selectOptionKeys.length === 0) {
        return;
      }

      const fetchedOptions = {};
      for (const key of selectOptionKeys) {
        if (externalData && Array.isArray(externalData[key])) {
          continue;
        }
        try {
          const endpoint = LOOKUP_ENDPOINT_MAP[key] || key;
          const api = new DataApi(endpoint);
          const response = await api.fetchAll();
          const payload = response?.data ?? [];
          fetchedOptions[key] = normalizeListResponse(payload);
        } catch (error) {

          fetchedOptions[key] = [];
        }
      }

      if (isMounted && Object.keys(fetchedOptions).length > 0) {
        setLookupOptions((prev) => ({ ...prev, ...fetchedOptions }));
      }
    };

    loadLookupData();

    return () => {
      isMounted = false;
    };
  }, [selectOptionKeys, externalData]);

  const normalizeLookupPath = (path = "") => {
    return path.replace(/^\/+|\/+$/g, "");
  };

  const getLookupTargetId = (lookupConfig = {}, record = {}) => {
    if (typeof lookupConfig.idResolver === "function") {
      return lookupConfig.idResolver(record);
    }

    if (
      lookupConfig.idField &&
      Object.prototype.hasOwnProperty.call(record, lookupConfig.idField)
    ) {
      return record[lookupConfig.idField];
    }

    if (
      lookupConfig.moduleIdField &&
      Object.prototype.hasOwnProperty.call(record, lookupConfig.moduleIdField)
    ) {
      return record[lookupConfig.moduleIdField];
    }

    if (lookupConfig.module) {
      const fallbackField = `${lookupConfig.module.replace(/s$/, "")}_id`;
      if (Object.prototype.hasOwnProperty.call(record, fallbackField)) {
        return record[fallbackField];
      }
    }

    return record.id;
  };

  const getLookupLabel = (value, record, lookupConfig = {}) => {
    if (typeof lookupConfig.labelResolver === "function") {
      return lookupConfig.labelResolver(record);
    }

    if (
      lookupConfig.labelField &&
      Object.prototype.hasOwnProperty.call(record, lookupConfig.labelField)
    ) {
      return record[lookupConfig.labelField] ?? value;
    }

    return value ?? "—";
  };

  // const getSelectOptions = useCallback(
  //   (field) => {
  //     if (!field || field.type !== "select" || !field.options) {
  //       return [];
  //     }
  //     if (Array.isArray(field.options)) {
  //       return field.options;
  //     }
  //     return externalData?.[field.options] || lookupOptions?.[field.options] || [];
  //   },
  //   [externalData, lookupOptions]
  // );


  const getSelectOptions = useCallback(
    (field, currentData) => {
      if (!field || field.type !== "select" || !field.options) {
        return [];
      }

      // ✅ If options is a function → execute it
      if (typeof field.options === "function") {
        return field.options(currentData || tempData || data || {});
      }

      // ✅ Static array
      if (Array.isArray(field.options)) {
        return field.options;
      }

      // ✅ Lookup string
      return externalData?.[field.options] || lookupOptions?.[field.options] || [];
    },
    [externalData, lookupOptions, tempData, data]
  );

  const handleLookupNavigation = (lookupConfig, record, event = null) => {
    if (event) {
      event.preventDefault();
    }

    if (!lookupConfig) return;

    const targetId = getLookupTargetId(lookupConfig, record);
    if (!targetId) return;

    const basePath = lookupConfig.path || lookupConfig.module;
    if (!basePath) return;

    const finalPath = normalizeLookupPath(basePath);
    if (!finalPath) return;

    const targetUrl = `/${finalPath}/${targetId}`;

    if (lookupConfig.newTab) {
      window.open(targetUrl, "_blank");
      return;
    }

    navigate(targetUrl);
  };


  const togglePasswordVisibility = (key) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getSelectDisplayValue = (field, value, currentData) => {
    if (!field || !value || !field.options) return value || "—";
    
    let options = [];
    try {
      if (typeof field.options === "function") {
        options = field.options(currentData || data || {}) || [];
      } else if (Array.isArray(field.options)) {
        options = field.options;
      }
      
      console.log("getSelectDisplayValue", { fieldKey: field.key, value, optionsLength: options.length });
      
      const matchedOption = options.find(opt => {
        const optVal = getOptionValue(opt);
        return optVal?.toString() === value.toString();
      });
      
      const display = matchedOption ? getOptionDisplayLabel(matchedOption, field) : value;
      console.log("Matched:", matchedOption ? matchedOption.label : "NO MATCH", "Display:", display);
      return display;
    } catch (e) {
      console.error("getSelectDisplayValue error:", e);
      return value || "—";
    }
  };


 const formatValue = (value, field) => {
    // 🌟 THE FIX: Move the custom render check to the VERY TOP!
    // This allows your smart render functions to use row data even if the primary value is empty.
    if (field.render && typeof field.render === "function") {
      const renderedValue = field.render(value, data, externalData);
      if (renderedValue !== undefined && renderedValue !== null && renderedValue !== "") {
        return renderedValue;
      }
    }

    // Default fallback if value is truly empty and no render function helped
    if (value === null || value === undefined || value === "") return "—";

    if (field.type === "toggle") {
      const boolValue = Boolean(value);
      return (
        <Badge bg={boolValue ? "success" : "danger"}>
          {boolValue ? "Active" : "Inactive"}
        </Badge>
      );
    }

    if (
      field.key.includes("user") ||
      field.key.includes("byid") ||
      field.key.includes("by_id")
    ) {
      const userId = value;
      if (userId && userNames[userId]) {
        return (
          <UserAvatar userId={userId} size={32} showName={true} clickable={true} />
        );
      }
    }

    const lookupConfig = lookupNavigation && lookupNavigation[field.key];
    if (lookupConfig && data) {
      const targetId = getLookupTargetId(lookupConfig, data);
      if (targetId) {
        const label = getLookupLabel(value, data, lookupConfig);
        return (
          <a
            href="#"
            onClick={(e) => handleLookupNavigation(lookupConfig, data, e)}
            style={{
              color: "var(--primary-color)",
              textDecoration: "none",
              fontWeight: "500",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.target.style.textDecoration = "underline")
            }
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
          >
            {label}
          </a>
        );
      }
    }

    // SELECT fields: lookup display name from options
    if (field.type === "select") {
      return getSelectDisplayValue(field, value, data);
    }

    if (field.type === "date") {
      try {
        const converted = convertToUserTimezone(value, timeZone);
        const datePart = converted.split(" ")[0];
        const [year, month, day] = datePart.split("-");
        return `${day}/${month}/${year}`;
      } catch {
        return "Invalid DateTime";
      }
    }
    if (field.type === "datetime") {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return value;
      }
    }
    if (field.type === "boolean") {
      return value ? "Yes" : "No";
    }
    if (field.type === "badge") {
      const badgeConfig = field.badgeConfig || {};
      const bgColor = badgeConfig[value] || (value ? "primary" : "danger");
      const label = badgeConfig[`${value}_label`] || (value ? "Active" : "Inactive");
      return <Badge style={{ borderRadius: "5px", padding: "2px" }} bg={bgColor}>{label}</Badge>;
    }
    if (field.type === "currency") {
      return `₹${parseFloat(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    if (field.type === "number") {
      return parseFloat(value).toLocaleString("en-IN");
    }

    return String(value);
  };


  useEffect(() => {
    setTempData(location?.state?.rowData);
  }, [location?.state?.isEdit]);

  const handleEdit = () => {
    if (onEdit) {
      onEdit(data);
    } else {
      setIsEditing(true);
      setTempData({
        ...data,
        isactive: data.isactive !== undefined ? Boolean(data.isactive) : true,
      });
    }
  };

  // const handleSave = async () => {

  //   console.log("hello check");
  //   console.log("tempDataCheck-->", tempData);

  //   //  if (customHandlers.beforeSave) {
  //   //         const customResult = customHandlers.beforeSave(formData, editingItem);
  //   //         if (customResult === false) return;
  //   //     }

  //   //  if (validationRules) {
  //   //             const errors = validationRules(formData, data, editingItem);
  //   //             console.log("validationRules",validationRules);
  //   //             console.log("errors",errors);
  //   //             if (errors.length > 0) {
  //   //                 PubSub.publish("RECORD_ERROR_TOAST", {
  //   //                     title: "Validation Error",
  //   //                     message: errors.join(", "),
  //   //                 });
  //   //                 return;
  //   //             }
  //   //         }

  //   try {
  //     setSaving(true);

  //     const hasFileUpload = Object.entries(tempData).some(([key, value]) => {
  //       if (key === "image") {
  //         return value instanceof File || value === null || value === "null";
  //       }
  //       return false;
  //     });

  //     const api = new DataApi(moduleApi);
  //     let response;

  //     if (hasFileUpload) {
  //       const formDataToSend = new FormData();

  //       console.log("tempData-->", tempData);
  //       console.log("formDataToSend before loop-->", formDataToSend);

  //       for (const [key, value] of Object.entries(tempData)) {
  //         if (key === "image") {
  //           if (value instanceof File) {

  //             formDataToSend.append("image", value);
  //           } else if (value === null || value === "null") {

  //             formDataToSend.append("image", "");
  //           } else if (typeof value === "string" && value) {

  //             formDataToSend.append("image", value);
  //           }
  //         } else {
  //           if (value !== null && value !== undefined) {
  //             if (typeof value === "object") {
  //               formDataToSend.append(key, JSON.stringify(value));
  //             } else {
  //               formDataToSend.append(key, value);
  //             }
  //           }
  //         }
  //       }


  //       for (let pair of formDataToSend.entries()) {

  //       }


  //       response = await api.updateFormData(formDataToSend, id);
  //     } else {
  //       console.log("check")
  //       const cleanData = { ...tempData };
  //       Object.keys(cleanData).forEach((key) => {
  //         if (cleanData[key] === "" || cleanData[key] === "null") {
  //           cleanData[key] = null;
  //         }
  //       });

  //       console.log("cleandata", cleanData)
  //       console.log("ide", id)

  //       response = await api.update(cleanData, id);
  //       console.log("response", response)
  //     }



  //     if (response && response.data) {
  //       await fetchData();
  //       PubSub.publish("RECORD_SAVED_TOAST", {
  //         title: "Success",
  //         message: `${moduleLabel} updated successfully`,
  //       });
  //       if (moduleName === "user") {
  //         PubSub.publish("USER_UPDATED", { user: response.data });
  //       }
  //       setIsEditing(false);
  //     }
  //   } catch (err) {
  //     let errorMessage = err.message;
  //     console.log("Error during save:", err);
  //     console.log("Error response data:", errorMessage);

  //     if (err.response && err.response.data && err.response.data.errors) {
  //       errorMessage = err.response.data.errors;
  //     }

  //     PubSub.publish("RECORD_ERROR_TOAST", {
  //       title: "Update Failed",
  //       message: ` ${moduleLabel}: ${errorMessage}`,
  //     });

  //     //  PubSub.publish("RECORD_ERROR_TOAST", {
  //     //   title: "Update Failed",
  //     //   message: `Failed to update ${moduleLabel}: ${errorMessage}`,
  //     // });
  //   } finally {
  //     setSaving(false);
  //   }
  // };


  const handleSave = async () => {

    console.log("hello check");
    console.log("tempDataCheck-->", tempData);


    // ✅ VALIDATION START
    if (validationRules) {
      const errors = validationRules(tempData, data);

      console.log("validationRules", validationRules);
      console.log("errors", errors);

      if (errors.length > 0) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Validation Error",
          message: errors.join(", "),
        });
        return; // ❌ STOP SAVE
      }
    }
    // ✅ VALIDATION END

    try {
      setSaving(true);

      const hasFileUpload = Object.entries(tempData).some(([key, value]) => value instanceof File);

      const api = new DataApi(moduleApi);
      let response;

      if (hasFileUpload) {
        const formDataToSend = new FormData();

        for (const [key, value] of Object.entries(tempData)) {
          if (value instanceof File) {
            formDataToSend.append(key, value);
          } else if (value !== null && value !== undefined) {
            if (typeof value === "object" && !(value instanceof File)) {
              formDataToSend.append(key, JSON.stringify(value));
            } else {
              formDataToSend.append(key, value);
            }
          }
        }

        response = await api.updateFormData(formDataToSend, id);

      } else {
        const cleanData = { ...tempData };

        Object.keys(cleanData).forEach((key) => {
          if (cleanData[key] === "" || cleanData[key] === "null") {
            cleanData[key] = null;
          }
        });

        response = await api.update(cleanData, id);
      }

      if (response && response.data) {
        await fetchData();

        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Success",
          message: `${moduleLabel} updated successfully`,
        });

        if (moduleName === "user") {
          PubSub.publish("USER_UPDATED", { user: response.data });
        }

        setIsEditing(false);
      }

    } catch (err) {
      let errorMessage = err.message;

      if (err.response && err.response.data && err.response.data.errors) {
        errorMessage = err.response.data.errors;
      }

      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Update Failed",
        message: `${moduleLabel}: ${errorMessage}`,
      });

    } finally {
      setSaving(false);
    }
  };


  const handleCancel = () => {
    setIsEditing(false);
    setTempData(null);
  };


  // const handleFieldChange = (fieldKey, value, field) => {
  //   console.log("ModuleDetail handleFieldChange:", fieldKey, value, field);

  //   if (!isEditing || !tempData) return;

  //   // 🔥 Custom onChange handler support (VendorDetail's cascading)
  //   if (field && typeof field.onChange === "function") {
  //     field.onChange(fieldKey, value, tempData, setTempData);
  //     return;
  //   }

  //   // ✅ Create updatedData FIRST
  //   let updatedData = {
  //     ...tempData,
  //     [fieldKey]: value,
  //   };

  //   // ✅ Reset dependent dropdowns (fallback)
  //   if (fieldKey === "country") {
  //     updatedData.state = "";
  //     updatedData.city = "";
  //   } else if (fieldKey === "state") {
  //     updatedData.city = "";
  //   }

  //   // ✅ Auto timezone & currency logic
  //   if (fieldKey === "country" || fieldKey === "country_code") {
  //     const countryInfo = getCountryInfo(value);

  //     if (countryInfo.timezone) {
  //       ["timezone", "time_zone", "timeZone"].forEach((key) => {
  //         if (updatedData.hasOwnProperty(key)) {
  //           updatedData[key] = countryInfo.timezone;
  //         }
  //       });
  //     }

  //     if (countryInfo.currency) {
  //       ["currency", "currency_code", "currencyCode"].forEach((key) => {
  //         if (updatedData.hasOwnProperty(key)) {
  //           updatedData[key] = countryInfo.currency;
  //         }
  //       });
  //     }
  //   }

  //   // ✅ Purchase calculation logic
  //   if (moduleName === "purchase") {
  //     if (fieldKey === "quantity" || fieldKey === "unit_price") {
  //       const quantity = parseFloat(updatedData.quantity) || 0;
  //       const unitPrice = parseFloat(updatedData.unit_price) || 0;
  //       updatedData.total_amount = (quantity * unitPrice).toFixed(2);
  //     }
  //   }

  //   setTempData(updatedData);
  // };


const handleFieldChange = (fieldKey, value, field) => {
    console.log("ModuleDetail handleFieldChange:", fieldKey, value, field);

    if (!isEditing || !tempData) return;

    // 🔥 Custom onChange handler support (Field-level)
    if (field && typeof field.onChange === "function") {
      field.onChange(fieldKey, value, tempData, setTempData);
      return;
    }

    // ✅ Create updatedData FIRST
    let updatedData = {
      ...tempData,
      [fieldKey]: value,
    };

    // ✅ Reset dependent dropdowns & auto-fill country code
    if (fieldKey === "country") {
      updatedData.state = "";
      updatedData.city = "";

      // 🌟 NEW LOGIC: Auto-fill Country Code 🌟
      if (value) {
        const selectedCountry = Country.getAllCountries().find(
          (c) => c.name === value
        );
        if (selectedCountry && selectedCountry.phonecode) {
          updatedData.country_code = `+${selectedCountry.phonecode}`;
        }
      } else {
        updatedData.country_code = ""; // Clear if country is deselected
      }
    } else if (fieldKey === "state") {
      updatedData.city = "";
    }

    // ✅ Auto timezone & currency logic
    if (fieldKey === "country" || fieldKey === "country_code") {
      const countryInfo = getCountryInfo(value);

      if (countryInfo.timezone) {
        ["timezone", "time_zone", "timeZone"].forEach((key) => {
          if (updatedData.hasOwnProperty(key)) {
            updatedData[key] = countryInfo.timezone;
          }
        });
      }

      if (countryInfo.currency) {
        ["currency", "currency_code", "currencyCode"].forEach((key) => {
          if (updatedData.hasOwnProperty(key)) {
            updatedData[key] = countryInfo.currency;
          }
        });
      }
    }

    // ✅ Purchase calculation logic
    if (moduleName === "purchase") {
      if (fieldKey === "quantity" || fieldKey === "unit_price") {
        const quantity = parseFloat(updatedData.quantity) || 0;
        const unitPrice = parseFloat(updatedData.unit_price) || 0;
        updatedData.total_amount = (quantity * unitPrice).toFixed(2);
      }
    }

    setTempData(updatedData);
  };
  const getFieldValue = (field, currentData) => {
    if (!currentData) return "";
    const value = currentData[field.key];

    if (isEditing) {
      if (field.type === "toggle") {
        return Boolean(value);
      }

      if (field.type === "date" && value) {
        try {
          const date = new Date(value);
          return date.toISOString().split("T")[0];
        } catch {
          return value;
        }
      }
      if (field.type === "datetime" && value) {
        try {
          const date = new Date(value);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch {
          return value;
        }
      }
      return value || "";
    } else {
      return formatValue(value, field) || "—";
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      const api = new DataApi(moduleApi);
      await api.delete(id);
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Success",
        message: `${moduleLabel} deleted successfully`,
      });
      navigate(`/${moduleName}`);
    } catch (error) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: `Failed to delete ${moduleLabel} ${error.message}`,
      });
    }
  };

  const handleBack = () => {
    console.log("Location State on Back:");
    const callfrom = location?.state?.call;

    console.log("callfrom", callfrom);

    if (callfrom == "bookIssue") {
      navigate(`/bookissue`);
    }
    else if (callfrom == "bookSubmission") {
      navigate(`/booksubmit`);
    }
    else {
      navigate(`/${moduleName}`);
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <ScrollToTop />
      </Container>
    );
  }

  if (!data) {
    return (
      <Container fluid>
        <ScrollToTop />
        <Card>
          <Card.Body className="text-center">
            <h4>No Data Found</h4>
            <p>Unable to load {moduleLabel} details.</p>
            <Button variant="primary" onClick={() => navigate(`/${moduleName}`)}>
              Back to List
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }


  const normalizedFields = {
    details: fields?.details || [],
  };

  const normalizedOtherFields = {
    other: fields?.other || [],
  };

  const normalizedAddressFields = {
    address: fields?.address || [],
  };

  const splitInto3 = (arr = []) => {
    const col1 = [];
    const col2 = [];
    const col3 = [];

    arr.forEach((item, index) => {
      if (index % 3 === 0) col1.push(item);
      else if (index % 3 === 1) col2.push(item);
      else col3.push(item);
    });

    return [col1, col2, col3];
  };

  const [col1, col2, col3] = splitInto3(normalizedFields.details);
  const [colother1, colother2, colother3] = splitInto3(
    normalizedOtherFields.other
  );
  const [coladd1, coladd2, coladd3] = splitInto3(
    normalizedAddressFields?.address
  );

  // const renderField = (field, index, currentData) => {
  //   if (!field || !field.key) return null;



  //   const isNonEditableField = nonEditableFields.includes(field.key);
  //   const shouldShowAsReadOnly = isEditing && isNonEditableField;



  //   if (field.type === "toggle") {
  //     const value = currentData ? Boolean(currentData[field.key]) : false;

  //     return (
  //       <Form.Group key={index} className="mb-3">
  //         <Form.Label className="fw-semibold">
  //           {isEditing ? field.label : null}
  //         </Form.Label>



  //         {isEditing && !isNonEditableField && (
  //           <div
  //             className="custom-toggle mt-2"
  //             onClick={() => handleFieldChange(field.key, !value)}
  //             style={{
  //               width: "60px",
  //               height: "30px",
  //               borderRadius: "20px",
  //               background: value ? "var(--primary-color)" : "#d1d5db",
  //               position: "relative",
  //               cursor: "pointer",
  //               transition: "0.3s",
  //             }}
  //           >
  //             <div
  //               style={{
  //                 width: "24px",
  //                 height: "24px",
  //                 background: "#fff",
  //                 borderRadius: "50%",
  //                 position: "absolute",
  //                 top: "3px",
  //                 left: value ? "33px" : "3px",
  //                 transition: "0.3s",
  //                 boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  //               }}
  //             />
  //           </div>
  //         )}
  //       </Form.Group>
  //     );
  //   }

  //   if (
  //     (!isEditing || shouldShowAsReadOnly) &&
  //     (field.key === "createdbyid" || field.key === "lastmodifiedbyid")
  //   ) {
  //     const userId = currentData ? currentData[field.key] : null;
  //     if (userId) {
  //       return (
  //         <Form.Group key={index} className="mb-3">
  //           <Form.Label className="fw-semibold">{field.label}</Form.Label>
  //           <div className="form-control-plaintext p-0 border-0">
  //             <UserAvatar
  //               userId={userId}
  //               size={36}
  //               showName={true}
  //               clickable={true}
  //             />
  //           </div>
  //         </Form.Group>
  //       );
  //     }
  //   }


  //   if (field.type === "select" && field.options) {
  //     // const options = getSelectOptions(field);
  //     const options = getSelectOptions(field, isEditing ? tempData : data);
  //     const rawValue = currentData ? currentData[field.key] : null;
  //     const currentValue =
  //       rawValue === undefined || rawValue === null ? "" : rawValue.toString();

  //     // Check if field is disabled (supports function)
  //     const isFieldDisabled = typeof field.disabled === 'function' 
  //       ? field.disabled(tempData || data) 
  //       : field.disabled;

  //     return (
  //       <Form.Group key={index} className="mb-3">
  //         <Form.Label className="fw-semibold">{field.label}</Form.Label>
  //         {isEditing && !isNonEditableField ? (
  //           <Form.Select
  //             value={currentValue}
  //             onChange={(e) =>
  //               handleFieldChange(field.key, e.target.value || null, field)
  //             }
  //             disabled={isFieldDisabled}
  //           >
  //             <option value="">{isFieldDisabled ? `${field.label} (Category required)` : `Select ${field.label}`}</option>
  //             {options.map((option) => {
  //               const optionId = getOptionValue(option);
  //               const optionLabel = getOptionDisplayLabel(option);
  //               return (
  //                 <option key={optionId} value={optionId}>
  //                   {optionLabel}
  //                 </option>
  //               );
  //             })}
  //             {/* Add current value as option if not in list (for creatable) */}
  //             {field.creatable && currentValue && !options.find(opt => getOptionValue(opt) === currentValue) && (
  //               <option key={currentValue} value={currentValue}>{currentValue}</option>
  //             )}
  //           </Form.Select>
  //         ) : (
  //           <Form.Control
  //             type="text"
  //             readOnly
  //             value={field.render ? field.render(currentValue, currentData, externalData) : (() => {
  //               if (field.displayKey && data) return data[field.displayKey] || "—";
  //               const selected = options.find((opt) => {
  //                 const optionValue = toStringSafe(getOptionValue(opt));
  //                 const optValue =
  //                   opt.id || opt.value || opt.role_name || opt.name;
  //                 return (
  //                   optionValue === currentValue ||
  //                   toStringSafe(optValue) === currentValue
  //                 );
  //               });
  //               return selected
  //                 ? getOptionDisplayLabel(selected)
  //                 : field.displayKey && data
  //                   ? data[field.displayKey] || "—"
  //                   : "—";
  //             })()}
  //             style={{
  //               pointerEvents: "none",
  //               opacity: 0.9,
  //             }}
  //           />
  //         )}
  //       </Form.Group>
  //     );
  //   }


  //   if (field.type === "password") {
  //     const rawValue = currentData ? currentData[field.key] : "";
  //     const isVisible = passwordVisibility[field.key] || false;

  //     return (
  //       <Form.Group key={index} className="mb-3">
  //         <Form.Label className="fw-semibold">{field.label}</Form.Label>
  //         {!isEditing || isNonEditableField ? (

  //           <div className="form-control-plaintext">******</div>
  //         ) : (
  //           <InputGroup>
  //             <Form.Control
  //               type={isVisible ? "text" : "password"}
  //               value={rawValue || ""}
  //               onChange={(e) =>
  //                 handleFieldChange(field.key, e.target.value)
  //               }
  //               style={{
  //                 backgroundColor: isNonEditableField ? "#f8f9fa" : "#fff",
  //                 borderRight: "none",
  //               }}
  //             />
  //             <InputGroup.Text
  //               style={{
  //                 backgroundColor: isNonEditableField ? "#f8f9fa" : "#fff",
  //                 borderLeft: "none",
  //                 cursor: isNonEditableField ? "default" : "pointer",
  //               }}
  //               onClick={() =>
  //                 !isNonEditableField && togglePasswordVisibility(field.key)
  //               }
  //             >
  //               <i
  //                 className={
  //                   isVisible ? "fa-solid fa-eye" : "fa-solid fa-eye-slash"
  //                 }
  //               ></i>
  //             </InputGroup.Text>
  //           </InputGroup>
  //         )}
  //       </Form.Group>
  //     );
  //   }

  //   const fieldValue = shouldShowAsReadOnly
  //     ? formatValue(currentData[field.key], field)
  //     : getFieldValue(field, currentData);
  //   const isElementValue = React.isValidElement(fieldValue);
  //   const controlType =
  //     isEditing && !isNonEditableField
  //       ? field.type === "number"
  //         ? "number"
  //         : field.type === "date"
  //           ? "date"
  //           : field.type === "datetime"
  //             ? "datetime-local"
  //             : "text"
  //       : "text";

  //   if ((!isEditing || shouldShowAsReadOnly) && isElementValue) {
  //     return (
  //       <Form.Group key={index} className="mb-3">
  //         <Form.Label className="fw-semibold">{field.label}</Form.Label>
  //         <div className="form-control-plaintext">{fieldValue}</div>
  //       </Form.Group>
  //     );
  //   }

  //   return (
  //     <Form.Group key={index} className="mb-3">
  //       <Form.Label className="fw-semibold">{field.label}</Form.Label>
  //       <Form.Control
  //         type={controlType}
  //         value={isElementValue ? "" : fieldValue ?? ""}
  //         readOnly={!isEditing || isNonEditableField}
  //         onChange={(e) => {
  //           if (!isEditing || isNonEditableField) return;

  //           let newValue = e.target.value;
  //           if (field.type === "number")
  //             newValue = newValue ? parseFloat(newValue) : null;
  //           if (field.type === "datetime")
  //             newValue = new Date(newValue).toISOString();

  //           handleFieldChange(field.key, newValue);
  //         }}
  //         style={{
  //           pointerEvents:
  //             isEditing && !isNonEditableField ? "auto" : "none",
  //           opacity: isEditing && !isNonEditableField ? 1 : 0.9,
  //           backgroundColor:
  //             isNonEditableField && isEditing ? "#f8f9fa" : "white",
  //         }}
  //       />
  //       {isNonEditableField && isEditing && (
  //         <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
  //           This field cannot be edited
  //         </Form.Text>
  //       )}
  //     </Form.Group>
  //   );
  // };


  const renderField = (field, index, currentData) => {
    if (!field || !field.key || !currentData) return null;

    const isNonEditableField = nonEditableFields.includes(field.key);
    const shouldShowAsReadOnly = isEditing && isNonEditableField;

    // Check if field is disabled or readOnly (supports function for disabled)
    const isFieldDisabled =
      (typeof field.disabled === "function"
        ? field.disabled(tempData || data, isEditing)
        : field.disabled) || field.readOnly;

    // Reusable Label Renderer
    const renderLabel = () => (
      <Form.Label className="">
        {field.label}
        {field.required && (
          <span className="text-danger ms-1">*</span>
        )}
      </Form.Label>
    );

    //  TOGGLE
    if (field.type === "toggle") {
      const value = currentData ? Boolean(currentData[field.key]) : false;

      return (
        <Form.Group key={index} className="mb-3">
          {isEditing && renderLabel()}

          {isEditing && !isNonEditableField && (
            <div
              className="custom-toggle mt-2"
              onClick={() => handleFieldChange(field.key, !value)}
              style={{
                width: "60px",
                height: "30px",
                borderRadius: "20px",
                background: value ? "var(--primary-color)" : "#d1d5db",
                position: "relative",
                cursor: "pointer",
                transition: "0.3s",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  background: "#fff",
                  borderRadius: "50%",
                  position: "absolute",
                  top: "3px",
                  left: value ? "33px" : "3px",
                  transition: "0.3s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </div>
          )}
        </Form.Group>
      );
    }

    //  CREATED BY / MODIFIED BY
    if (
      (!isEditing || shouldShowAsReadOnly) &&
      (field.key === "createdbyid" || field.key === "lastmodifiedbyid")
    ) {
      const userId = currentData ? currentData[field.key] : null;
      if (userId) {
        return (
          <Form.Group key={index} className="mb-3">
            {renderLabel()}
            <div className="form-control-plaintext p-0 border-0">
              <UserAvatar
                userId={userId}
                size={36}
                showName={true}
                clickable={true}
              />
            </div>
          </Form.Group>
        );
      }
    }

    //  SELECT
if (field.type === "select" && (field.options || field.asyncSelect)) {
      const options = field.asyncSelect ? [] : getSelectOptions(field, isEditing ? tempData : data);
      const rawValue = currentData ? currentData[field.key] : null;
      const currentValue = rawValue == null ? null : String(rawValue);

      // Safe helper to extract value, falling back to standard react-select properties
      const safeGetOptionValue = (opt) => {
        if (!opt) return null;
        if (typeof getOptionValue === 'function') {
          const val = getOptionValue(opt);
          if (val !== undefined && val !== null) return String(val);
        }
        return String(opt.value || opt.id || "");
      };

      // Find current selected option for display
      const selectedOption = currentValue
        ? options.find(opt => safeGetOptionValue(opt) === currentValue) || { 
            value: currentValue, 
            label: typeof getSelectDisplayValue === 'function' 
                   ? getSelectDisplayValue(field, currentValue, currentData) 
                   : currentValue 
          }
        : null;

      const customSelectStyles = {
        control: (base, state) => ({
          ...base,
          minHeight: '38px',
          borderColor: state.isFocused ? 'var(--primary-color)' : '#ced4da',
          boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(111, 66, 193, 0.25)' : 'none',
          '&:hover': { borderColor: 'var(--primary-color)' }
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected ? '#f8f9fa' : 
                          state.isFocused ? 'rgba(111, 66, 193, 0.08)' : 'white',
          color: state.isSelected ? '#374151' : 
                state.isFocused ? 'var(--primary-color)' : '#212529',
          cursor: 'pointer'
        }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        singleValue: (base) => ({ ...base, fontSize: '14px' })
      };

      const isFieldDisabled = typeof field.disabled === 'function' 
        ? field.disabled(tempData || data) 
        : field.disabled;

      return (
        <Form.Group key={index} className="mb-3">
          {renderLabel()}
          
          {isEditing && !isNonEditableField ? (
            field.asyncSelect ? (
              <AsyncSelect
                cacheOptions
                defaultOptions={field.defaultOptions !== undefined ? field.defaultOptions : true}
                loadOptions={field.loadOptions}
                
                // --- FIXED ONCHANGE ---
                onChange={(selected) => {
                  const val = selected ? (selected.value || selected.id) : null;
                  handleFieldChange(field.key, val, field);
                }}
                
                value={selectedOption}
                isDisabled={isFieldDisabled}
                placeholder={field.placeholder || `Select ${field.label}`}
                styles={customSelectStyles}
                getOptionLabel={option => typeof getOptionDisplayLabel === 'function' ? getOptionDisplayLabel(option, field) : (option.label || option.name)}
                getOptionValue={option => option.value || option.id}
              />
            ) : field.isSearchable ? (
              <Select
                value={selectedOption}
                
                // --- FIXED ONCHANGE ---
                onChange={(selected) => {
                  // We safely extract selected.value directly
                  const val = selected ? (selected.value || selected.id) : null;
                  handleFieldChange(field.key, val, field);
                }}
                
                options={options}
                isSearchable={true}
                isClearable={true}
                isDisabled={isFieldDisabled}
                placeholder={field.placeholder || `Search ${field.label.toLowerCase()}...`}
                styles={customSelectStyles}
                getOptionLabel={option => typeof getOptionDisplayLabel === 'function' ? getOptionDisplayLabel(option, field) : (option.label || option.name)}
                getOptionValue={option => option.value || option.id}
              />
            ) : (
              <Form.Select
                value={currentValue || ""}
                onChange={(e) => handleFieldChange(field.key, e.target.value || null, field)}
                disabled={isFieldDisabled}
              >
                <option value="">
                  {isFieldDisabled ? `${field.label} (Category required)` : `Select ${field.label}`}
                </option>

                {options.map((option) => {
                  const optionId = safeGetOptionValue(option);
                  const optionLabel = typeof getOptionDisplayLabel === 'function' ? getOptionDisplayLabel(option, field) : (option.label || option.name);
                  return (
                    <option key={optionId} value={optionId}>
                      {optionLabel}
                    </option>
                  );
                })}
              </Form.Select>
            )
          ) : (
            <Form.Control
              type="text"
              readOnly
              value={field.render 
                ? field.render(currentValue, currentData, externalData)
                : typeof getSelectDisplayValue === 'function' 
                  ? getSelectDisplayValue(field, currentValue, currentData) || "—" 
                  : currentValue || "—"
              }
              style={{ pointerEvents: "none", opacity: 0.9 }}
            />
          )}
        </Form.Group>
      );
    }
    //  IMAGE UPLOAD
    const isImageField = field.type === "image" || 
      (field.type === "url" && 
       (field.key.toLowerCase().includes("logo") || 
        field.key.toLowerCase().includes("image") ||
        field.key.toLowerCase().includes("picture")));

    if (isImageField) {
      const currentImageUrl = currentData[field.key];
      const currentFile = currentData[field.key];

      return (
        <Form.Group key={index} className="mb-3">
          {renderLabel()}
          
          {!isEditing || isNonEditableField ? (
            currentImageUrl ? (
              <div className="text-center">
                <img 
                  src={currentImageUrl.startsWith("http") ? currentImageUrl : `/${currentImageUrl}`}
                  alt={field.label}
                  style={{
                    maxWidth: "200px",
                    maxHeight: "100px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6"
                  }}
                />
                <div className="mt-2 small text-muted">{currentImageUrl}</div>
              </div>
            ) : (
              <div className="form-control-plaintext text-muted">No image</div>
            )
          ) : (
            <div>
              {currentFile instanceof File && (
                <div className="mb-2 p-2 bg-light border rounded">
                  <small className="text-success">
                    <i className="fa fa-check me-1"></i>
                    {currentFile.name} ({(currentFile.size/1024/1024).toFixed(1)} MB) selected
                  </small>
                </div>
              )}
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  handleFieldChange(field.key, file || null, field);
                }}
              />
              <Form.Text className="text-muted">
                JPG, PNG, GIF (max 5MB)
              </Form.Text>
            </div>
          )}
        </Form.Group>
      );
    }

    //  PASSWORD
    if (field.type === "password") {
      const rawValue = currentData ? currentData[field.key] : "";
      const isVisible = passwordVisibility[field.key] || false;

      return (
        <Form.Group key={index} className="mb-3">
          {renderLabel()}

          {!isEditing || isNonEditableField ? (
            <div className="form-control-plaintext">******</div>
          ) : (
            <InputGroup>
              <Form.Control
                type={isVisible ? "text" : "password"}
                value={rawValue || ""}
                onChange={(e) =>
                  handleFieldChange(field.key, e.target.value)
                }
              />
              <InputGroup.Text
                onClick={() =>
                  !isNonEditableField &&
                  togglePasswordVisibility(field.key)
                }
                style={{ cursor: "pointer" }}
              >
                <i
                  className={
                    isVisible
                      ? "fa-solid fa-eye"
                      : "fa-solid fa-eye-slash"
                  }
                />
              </InputGroup.Text>
            </InputGroup>
          )}
        </Form.Group>
      );
    }

    //  DEFAULT INPUT
    const fieldValue = shouldShowAsReadOnly
      ? formatValue(currentData[field.key], field)
      : getFieldValue(field, currentData);

    const isElementValue = React.isValidElement(fieldValue);

    const controlType =
      isEditing && !isNonEditableField && !isFieldDisabled
        ? field.type === "number"
          ? "number"
          : field.type === "date"
            ? "date"
            : field.type === "datetime"
              ? "datetime-local"
              : "text"
        : "text";

    if ((!isEditing || shouldShowAsReadOnly || isFieldDisabled) && isElementValue) {
      return (
        <Form.Group key={index} className="mb-3">
          {renderLabel()}
          <div className="form-control-plaintext">{fieldValue}</div>
        </Form.Group>
      );
    }

    return (
      <Form.Group key={index} className="mb-3">
        {renderLabel()}

        <Form.Control
          type={controlType}
          value={isElementValue ? "" : fieldValue ?? ""}
          readOnly={!isEditing || isNonEditableField || isFieldDisabled}
          onChange={(e) => {
            if (!isEditing || isNonEditableField || isFieldDisabled) return;

            let newValue = e.target.value;

            if (field.type === "number") {
              newValue = newValue ? parseFloat(newValue) : null;
            }

            if (field.type === "datetime") {
              newValue = new Date(newValue).toISOString();
            }

            handleFieldChange(field.key, newValue);
          }}
          style={{
            pointerEvents:
              isEditing && !isNonEditableField && !isFieldDisabled ? "auto" : "none",
            opacity: isEditing && !isNonEditableField && !isFieldDisabled ? 1 : 0.9,
            backgroundColor:
              (isNonEditableField || isFieldDisabled) && isEditing ? "#f8f9fa" : "white",
          }}
        />
        {/* 
        {(isNonEditableField || isFieldDisabled) && isEditing && (
          <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
            This field cannot be edited
          </Form.Text>
        )} */}
      </Form.Group>
    );
  };


  return (
    <>
      <Container fluid className="py-4" style={{ marginTop: "-20px" }}>
        <ScrollToTop />

        <Row className="justify-content-center">
          <Col lg={12} xl={12}>
            <Card className="border-0 shadow-sm detail-h4">
              <Card.Body>
                <div
                  className="d-flex justify-content-between align-items-center mb-4 p-2"
                  style={{
                    color: "var(--primary-color)",
                    background: "var(--primary-background-color)",
                    borderRadius: "10px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <button
                      onClick={handleBack}
                      className="shadow-sm d-flex align-items-center justify-content-center custom-btn-back"
                    >
                      <i className="fa-solid fa-arrow-left"></i>
                    </button>

                    <h5
                      className="fw-bold mb-0"
                      style={{ color: "var(--primary-color)" }}
                    >
                      {icon && <i className={`${icon} me-2 fs-6`}></i>}
                      {moduleLabel} Detail
                    </h5>
                  </div>

                  {/* Right Side - Action Buttons */}

                  <div className="d-flex gap-2">
                    {!isEditing ? (
                      <>
                        {allowEdit && (
                          <button
                            className="custom-btn-primary"
                            onClick={handleEdit}
                          >
                            <i className="fa-solid fa-edit me-2"></i>
                            Edit {moduleLabel}
                          </button>)}
                        {/* <button
                            className="custom-btn-delete-detail"
                            onClick={handleDelete}
                          >
                            <i className="fa-solid fa-trash me-2"></i>
                            Delete
                          </button> */}
                      </>
                    ) : (
                      <div className="d-flex gap-2">
                        <button
                          className="custom-btn-primary"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          <i className="fa-solid fa-check me-2"></i>
                          {saving ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="custom-btn-secondary"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          <i className="fa-solid fa-times me-2"></i>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Details Section */}
                <Row className="mt-4">
                  {/* <Col md={12} className="mb-4">
                    <h6
                      className="fw-bold mb-0 d-flex align-items-center p-3 border rounded"
                      style={{
                        color: "var(--primary-color)",
                        background: "vae(--primary-background-color)",

                        borderRadius: "10px",
                      }}
                    >
                      {moduleLabel} Information
                      <Badge bg={data?.status || data?.is_active ? "success" : "danger"} className="mx-2 float-end">
                        {data?.status || data?.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </h6>
                  </Col> */}
                  <Col md={12} className="mb-4">
                    <h6
                      className="fw-bold mb-0 d-flex align-items-center p-3 border rounded"
                      style={{
                        color: "var(--primary-color)",
                        background: "var(--primary-background-color)",
                        borderRadius: "10px",
                      }}
                    >
                      {moduleLabel} Information

                      {/* {(data?.status !== undefined || data?.is_active !== undefined) && (
                        <Badge
                          bg={(data?.status || data?.is_active) ? "success" : "danger"}
                          className="mx-2 float-end"
                        >
                          {(data?.status || data?.is_active) ? "Active" : "Inactive"}
                        </Badge>
                      )} */}

                      {!isEditing ? (
                        (data?.status !== undefined || data?.is_active !== undefined) && (
                          <Badge
                            bg={(data?.status || data?.is_active) ? "success" : "danger"}
                            className="mx-2 float-end"
                          >
                            {(data?.status || data?.is_active) ? "Active" : "Inactive"}
                          </Badge>
                        )
                      ) : (
                        <></>
                      )}
                    </h6>
                  </Col>

                  <Row className="px-5">
                    {normalizedFields && normalizedFields.details && (
                      <Row>
                        {[col1, col2, col3].map((columnFields, colIndex) => (
                          <Col md={4} key={colIndex}>
                            {columnFields.map((field, index) =>
                              renderField(
                                field,
                                index,
                                isEditing ? tempData : data
                              )
                            )}
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Row>
                </Row>

                {/* Address Section */}
                {normalizedAddressFields &&
                  normalizedAddressFields?.address?.length > 0 && (
                    <Row className="mt-4">
                      <Col md={12} className="mb-4">
                        <h6
                          className="fw-bold mb-0 d-flex align-items-center justify-content-between p-3 border rounded"
                          style={{
                            color: "var(--primary-color)",
                            background: "var(--primary-background-color)",
                            borderRadius: "10px",
                          }}
                        >
                          Address Information
                        </h6>
                      </Col>
                      <Row className="px-5">
                        {normalizedAddressFields &&
                          normalizedAddressFields.address && (
                            <Row>
                              {[coladd1, coladd2, coladd3].map(
                                (columnFields, colIndex) => (
                                  <Col md={4} key={colIndex}>
                                    {columnFields.map((field, index) =>
                                      renderField(
                                        field,
                                        index,
                                        isEditing ? tempData : data
                                      )
                                    )}
                                  </Col>
                                )
                              )}
                            </Row>
                          )}
                      </Row>
                    </Row>
                  )}

                {/* Other Section */}
                {normalizedOtherFields.other.length > 0 && (
                  <Row className="mt-4">
                    <Col md={12} className="mb-4">
                      <h6
                        className="fw-bold mb-0 d-flex align-items-center justify-content-between p-3 border rounded"
                        style={{
                          color: "var(--primary-color)",
                          background: "var(--primary-background-color)",
                          borderRadius: "10px",
                        }}
                      >
                        Others
                      </h6>
                    </Col>
                    <Row className="px-5">
                      {normalizedOtherFields &&
                        normalizedOtherFields.other && (
                          <Row>
                            {[colother1, colother2, colother3].map(
                              (columnFields, colIndex) => (
                                <Col md={4} key={colIndex}>
                                  {columnFields.map((field, index) =>
                                    renderField(
                                      field,
                                      index,
                                      isEditing ? tempData : data
                                    )
                                  )}
                                </Col>
                              )
                            )}
                          </Row>
                        )}
                    </Row>
                  </Row>
                )}

                {/* Related Modules */}
                {relatedModules.length > 0 && (
                  <Row className="mb-4">
                    <Col>
                      <Card
                        style={{
                          border: "none",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                          borderRadius: "12px",
                          overflow: "hidden",
                        }}
                      >
                        <Card.Body style={{ padding: 0 }}>
                          <div
                            style={{
                              padding: "20px 24px",
                              borderBottom: "1px solid #e9ecef",
                              background:
                                "linear-gradient(to right, #f8f9fa, #ffffff)",
                            }}
                          >
                            <h6
                              style={{
                                margin: 0,
                                color: "var(--primary-color)",
                                fontSize: "16px",
                                fontWeight: "700",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <i className="fa-solid fa-link"></i>
                              Related Records
                            </h6>
                          </div>

                          <div style={{ padding: "24px" }}>
                            {relatedModules.map((relatedModule, idx) => {
                              const normalizedModule =
                                typeof relatedModule === "string"
                                  ? {
                                    key: relatedModule,
                                    label:
                                      relatedModule
                                        .charAt(0)
                                        .toUpperCase() +
                                      relatedModule.slice(1),
                                  }
                                  : relatedModule;

                              const moduleKey =
                                normalizedModule.key || normalizedModule;
                              const moduleLabel =
                                normalizedModule.label ||
                                (typeof normalizedModule === "string"
                                  ? normalizedModule
                                    .charAt(0)
                                    .toUpperCase() +
                                  normalizedModule.slice(1)
                                  : "Related");

                              return (
                                <div
                                  key={idx}
                                  className={idx > 0 ? "mt-4 pt-4" : ""}
                                  style={
                                    idx > 0
                                      ? { borderTop: "1px solid #e9ecef" }
                                      : {}
                                  }
                                >
                                  <h6
                                    style={{
                                      marginBottom: "16px",
                                      color: "#495057",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {moduleLabel}
                                  </h6>
                                  {relatedData[moduleKey] &&
                                    relatedData[moduleKey].length > 0 ? (
                                    normalizedModule.render ? (
                                      normalizedModule.render(
                                        relatedData[moduleKey],
                                        data
                                      )
                                    ) : (
                                      <div className="table-responsive">
                                        <table
                                          style={{
                                            width: "100%",
                                            fontSize: "14px",
                                            borderCollapse: "collapse",
                                          }}
                                        >
                                          <thead>
                                            <tr
                                              style={{
                                                background: "#f8f9fa",
                                                borderBottom:
                                                  "2px solid #e9ecef",
                                              }}
                                            >
                                              {normalizedModule.columns?.map(
                                                (col, colIdx) => (
                                                  <th
                                                    key={colIdx}
                                                    style={{
                                                      padding: "12px 16px",
                                                      textAlign: "left",
                                                      fontWeight: "600",
                                                      color: "#495057",
                                                    }}
                                                  >
                                                    {col.label}
                                                  </th>
                                                )
                                              )}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {relatedData[moduleKey].map(
                                              (item, itemIdx) => (
                                                <tr
                                                  key={itemIdx}
                                                  style={{
                                                    borderBottom:
                                                      "1px solid #e9ecef",
                                                    transition:
                                                      "background-color 0.2s",
                                                  }}
                                                  onMouseEnter={(e) =>
                                                  (e.target.parentElement.style.background =
                                                    "#f8f9fa")
                                                  }
                                                  onMouseLeave={(e) =>
                                                  (e.target.parentElement.style.background =
                                                    "transparent")
                                                  }
                                                >
                                                  {normalizedModule.columns?.map(
                                                    (col, colIdx) => (
                                                      <td
                                                        key={colIdx}
                                                        style={{
                                                          padding:
                                                            "12px 16px",
                                                          color: "#6c757d",
                                                        }}
                                                      >
                                                        {item[col.key] || "N/A"}
                                                      </td>
                                                    )
                                                  )}
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )
                                  ) : (
                                    <div
                                      style={{
                                        padding: "32px 16px",
                                        textAlign: "center",
                                        background: "#f8f9fa",
                                        borderRadius: "8px",
                                        border: "1px dashed #dee2e6",
                                      }}
                                    >
                                      <i
                                        className="fa-solid fa-inbox"
                                        style={{
                                          fontSize: "32px",
                                          color: "#adb5bd",
                                          marginBottom: "8px",
                                          display: "block",
                                        }}
                                      ></i>
                                      <p
                                        style={{
                                          color: "#6c757d",
                                          margin: 0,
                                          fontSize: "14px",
                                        }}
                                      >
                                        No {moduleLabel.toLowerCase()} found.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <ConfirmationModal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          onConfirm={confirmDelete}
          title={`Delete `}
          message={`Are you sure you want to delete this?`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      </Container>
    </>
  );
};

export default ModuleDetail;  